import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@/boot/firebase';
import { AuthError } from '@/composables/useFirebaseFunctions';
import { FRONTEND_API_CONFIG } from '@/config/appConfig';
import { getTaipeiHour } from '@/composables/useStandardizedTimestamps';
import { shouldSendWarmPing } from '@/utils/warmOnUse';
import {
  CallTimeoutError,
  callDeadlineFor,
  warmAuthToken,
  withDeadline,
  TOKEN_WARMUP_DEADLINE_MS,
} from '@/utils/callDeadline';

type FunctionName =
  // Admin
  | 'getAggregatedClasses'
  | 'getAdminAggregatedClassDetails'
  | 'updateClassApprovalStatus'
  | 'updateClassCountsOnAttendanceChange'
  | 'batchSaveClassStatus'
  | 'toggleClassSavedStatus'
  | 'batchUpdateAttendanceData'
  | 'addStudentToClass'
  | 'updateAdminNotesWithHistory'
  | 'saveClassChangesWithHistory'
  | 'updateStudentPhoto'
  | 'updateFeedbackNotes'
  | 'getOnDutyAdminForDate'
  | 'setOnDutyAdminForDate'
  | 'checkFirstTrialStatus'
  | 'getJotformWebhookUrl'
  | 'getStudentPhotoUploadUrl'
  | 'getAvailableTimeSlots'
  | 'getTimeslotRoster'
  | 'getTimeSlotCriticalData'
  | 'getBestMatchTimeslot'
  | 'deleteManualStudent'
  | 'getScheduleUploadJobs'
  | 'getQuickPreviewData'
  | 'updateClassTeacherName'
  | 'supervisorSignOffClass'
  | 'supervisorUndoSignOff'
  | 'getInstructionGalleries'
  | 'createInstructionGallery'
  | 'getInstructionGalleryDetails'
  | 'updateInstructionGallery'
  | 'updateInstructionGalleryOrder'
  | 'updateInstructionGallerySlides'
  | 'deleteInstructionGallery'
  | 'getInstructionGalleriesForAudience'

  // Teacher
  | 'getClassesForTeacherView'
  | 'batchUpdateStudentNotes'
  | 'signOffOnClass'
  | 'signOffOnCoopClass'
  | 'batchSignOffOnClasses'
  | 'updateTeacherNotesWithHistory'
  | 'getAttendanceForClass'
  | 'getClassesForDate'
  | 'getClassCalendarDates'
  | 'getAggregatedClassDetails'
  | 'getInstructionGalleriesForTeacher'

  // Reviewed Classes (Lazy Loading)
  | 'getReviewedMonthSummaries'
  | 'getReviewedDateSummaries'
  | 'getReviewedClassDetails'
  | 'getReviewedTeachers'

  // Feedback
  // 體驗回饋佇列 hidden 2026-11 — docs/TRIAL_FEEDBACK_HUB_SPEC.md § 8 (D8): these six stay in the
  // type only because the kept FeedbackPreparationModal.vue still names them (vue-tsc checks every
  // .vue); their functionGroups entries are commented out, so a call throws before reaching the SDK.
  | 'addToFeedbackPreparationList'
  | 'removeFromFeedbackPreparationList'
  | 'updateFeedbackPreparationItem'
  | 'getFeedbackPreparationList'
  | 'processFeedbackPreparationList'
  | 'addManualFeedbackPreparationItem'
  | 'jotformWebhookHandler'
  | 'getTeachers'
  | 'getRecentClassesForFeedback'
  | 'getTodaysFirstTrialsWithStatus'
  | 'getFeedbackStatusForClass'
  | 'markFeedbackAsCompleted'
  | 'setFirstTrialVerdict'
  | 'createFeedbackForAttendance'
  | 'resendFeedbackLine'
  | 'getFirstTrialClassMarkers'
  | 'getPendingFirstTrials'
  | 'getFamilyFacts'
  | 'getCoopFeedbackTeachers'
  | 'setCoopFeedbackTeachers'
  | 'disableFeedbackItem'
  | 'checkJotformApprovalStatus'
  | 'resyncFeedbackNoteFromJotform'
  // 重新儲存 the Hub's PDF copy (tasks/todo-trial-feedback-hub.md W2.5, D20)
  | 'retryFeedbackPdfCopy'

  // Schedule
  | 'getMultiFileScheduleUploadUrls'
  | 'getScheduleUploadPreview'
  | 'synchronizeExcelData'
  | 'cancelScheduleUpload'
  | 'processResolvedAmbiguities'
  | 'getManualAddClassPreview'
  | 'commitManualAddClass'
  | 'getLatestScheduleUploadTime'

  // Users
  | 'onUserUpdatedSyncRoles'
  | 'createUser'
  | 'deleteUser'
  | 'getAllUsers'
  | 'updateUserData'
  | 'getUsersByRole'
  | 'getRecentClasses'
  | 'testLineId'

  // Employees — 臨時助教
  | 'createTempAssistant'
  | 'updateTempAssistant'
  | 'deactivateTempAssistant'
  | 'getTempAssistants'

  // 代課 record (助教紀錄)
  | 'getSubstitutePickList'
  | 'updateClassSubstitute'

  // Auth
  | 'loginKioskWithPin'
  | 'updateKioskPin'

  // Messaging
  | 'forceKioskUpdate'
  | 'triggerKioskReload'

  // Utility
  | 'fixAuthClaims'

  // Session enrollments (renewal-driven slot reservation workflow)
  | 'mutateSessionEnrollment'
  | 'getActiveSessionEnrollments'
  | 'getRenewalDueList'
  | 'getStudentStatusTimeline'
  // 名額使用情況 (tasks/todo-seat-usage-facts.md W1.6) — read-only facts grid; warm-on-use target
  | 'getSeatUsageFacts'
  | 'getMissedRenewals'
  | 'backfillSessionEnrollments'
  | 'runBackfillTimeslotId'
  | 'runDailyAutoArchive'
  | 'dismissUserBanner'

  // Program waitlist — transfer record management (Wave 2)
  | 'createTransferRecord'
  | 'mutateTransferRecord'
  | 'backfillTransferRecordDiscriminator'
  | 'getTargetExistingBookings'

  // Warm-on-use targets only (their real calls go through useFirebaseFunctions
  // with the deployed name) — 2026-09-29, moved off the scheduled warmer
  | 'addProgramWaitlistEntry'
  | 'exportBankTransferData'
  | 'listPaymentShortLinks'

  // Notes - Attendance
  | 'createAttendanceNote'
  | 'getAttendanceNotesByClass'
  | 'getAttendanceNotesHistory'
  | 'obsoleteAttendanceNote'
  | 'getAttendanceNotesByGroup'
  | 'getAttendanceNotesByDate'
  // Notes - Progress
  | 'createProgressNote'
  | 'getProgressNotesByStudent'
  | 'getProgressNotesByClass'
  | 'obsoleteProgressNote'
  | 'getProgressNoteCounts'
  // Notes - TimeSlot
  | 'updateTimeSlotNote'
  | 'getTimeSlotNote'
  | 'getTimeslotNoteHistoryByGroup'
  | 'obsoleteTimeslotNoteHistoryEntry'
  // Notes - Student Dashboard
  | 'getStudentDashboardData'
  | 'getStudentRecordsSummary'
  | 'getBatchStudentRecordsSummary';

const functionGroups: Record<string, string> = {
  // Admin
  getAggregatedClasses: 'admin',
  getAdminAggregatedClassDetails: 'admin',
  updateClassApprovalStatus: 'admin',
  updateClassCountsOnAttendanceChange: 'admin',
  batchSaveClassStatus: 'admin',
  toggleClassSavedStatus: 'admin',
  batchUpdateAttendanceData: 'admin',
  addStudentToClass: 'admin',
  updateAdminNotesWithHistory: 'admin',
  saveClassChangesWithHistory: 'admin',
  updateStudentPhoto: 'admin',
  updateFeedbackNotes: 'admin',
  getOnDutyAdminForDate: 'admin',
  setOnDutyAdminForDate: 'admin',
  checkFirstTrialStatus: 'admin',
  getJotformWebhookUrl: 'admin',
  getStudentPhotoUploadUrl: 'admin',
  getAvailableTimeSlots: 'admin',
  getTimeslotRoster: 'admin',
  getTimeSlotCriticalData: 'admin',
  getBestMatchTimeslot: 'admin',
  deleteManualStudent: 'admin',
  getScheduleUploadJobs: 'admin',
  getQuickPreviewData: 'admin',
  updateClassTeacherName: 'admin',
  supervisorSignOffClass: 'admin',
  supervisorUndoSignOff: 'admin',
  getInstructionGalleries: 'admin',
  createInstructionGallery: 'admin',
  getInstructionGalleryDetails: 'admin',
  updateInstructionGallery: 'admin',
  updateInstructionGalleryOrder: 'admin',
  updateInstructionGallerySlides: 'admin',
  deleteInstructionGallery: 'admin',
  getInstructionGalleriesForAudience: 'admin',
  getKioskScreensaverConfig: 'admin',
  saveKioskScreensaverConfig: 'admin',

  // Teacher
  getClassesForTeacherView: 'teacher',
  batchUpdateStudentNotes: 'teacher',
  signOffOnClass: 'teacher',
  signOffOnCoopClass: 'teacher',
  batchSignOffOnClasses: 'teacher',
  updateTeacherNotesWithHistory: 'teacher',
  getAttendanceForClass: 'teacher',
  getClassesForDate: 'teacher',
  getClassCalendarDates: 'teacher',
  getAggregatedClassDetails: 'teacher',
  getInstructionGalleriesForTeacher: 'teacher',

  // Reviewed Classes
  getReviewedMonthSummaries: 'reviewedClasses',
  getReviewedDateSummaries: 'reviewedClasses',
  getReviewedClassDetails: 'reviewedClasses',
  getReviewedTeachers: 'reviewedClasses',

  // Feedback
  // 體驗回饋佇列 hidden 2026-11 — docs/TRIAL_FEEDBACK_HUB_SPEC.md § 8 (D8); restore = uncomment.
  // addToFeedbackPreparationList: 'feedback',
  // removeFromFeedbackPreparationList: 'feedback',
  // updateFeedbackPreparationItem: 'feedback',
  // getFeedbackPreparationList: 'feedback',
  // processFeedbackPreparationList: 'feedback',
  // addManualFeedbackPreparationItem: 'feedback',
  jotformWebhookHandler: 'feedback',
  getTeachers: 'feedback',
  getRecentClassesForFeedback: 'feedback',
  getTodaysFirstTrialsWithStatus: 'feedback',
  getFeedbackStatusForClass: 'feedback',
  markFeedbackAsCompleted: 'feedback',
  setFirstTrialVerdict: 'feedback',
  createFeedbackForAttendance: 'feedback',
  resendFeedbackLine: 'feedback',
  getFirstTrialClassMarkers: 'feedback',
  getPendingFirstTrials: 'feedback',
  getFamilyFacts: 'feedback',
  getCoopFeedbackTeachers: 'feedback',
  setCoopFeedbackTeachers: 'feedback',
  disableFeedbackItem: 'feedback',
  checkJotformApprovalStatus: 'feedback',
  resyncFeedbackNoteFromJotform: 'feedback',
  retryFeedbackPdfCopy: 'feedback',

  // Schedule
  getMultiFileScheduleUploadUrls: 'schedule',
  getScheduleUploadPreview: 'schedule',
  synchronizeExcelData: 'schedule',
  cancelScheduleUpload: 'schedule',
  processResolvedAmbiguities: 'schedule',
  getManualAddClassPreview: 'schedule',
  commitManualAddClass: 'schedule',
  getLatestScheduleUploadTime: 'admin',

  // Users
  onUserUpdatedSyncRoles: 'users',
  createUser: 'users',
  deleteUser: 'users',
  getAllUsers: 'users',
  updateUserData: 'users',
  getUsersByRole: 'users',
  getRecentClasses: 'users',
  testLineId: 'users',

  // Employees — 臨時助教
  createTempAssistant: 'employees',
  updateTempAssistant: 'employees',
  deactivateTempAssistant: 'employees',
  getTempAssistants: 'employees',

  // 代課 record (助教紀錄)
  getSubstitutePickList: 'teacher',
  updateClassSubstitute: 'admin',

  // Auth
  loginKioskWithPin: 'auth',
  updateKioskPin: 'auth',

  // Messaging
  forceKioskUpdate: 'messaging',
  triggerKioskReload: 'messaging',

  // Utility
  fixAuthClaims: 'utility',

  // Session enrollments
  mutateSessionEnrollment: 'sessionEnrollments',
  getActiveSessionEnrollments: 'sessionEnrollments',
  getRenewalDueList: 'sessionEnrollments',
  getStudentStatusTimeline: 'sessionEnrollments',
  getSeatUsageFacts: 'sessionEnrollments',
  getMissedRenewals: 'sessionEnrollments',
  backfillSessionEnrollments: 'sessionEnrollments',
  runBackfillTimeslotId: 'sessionEnrollments',
  runDailyAutoArchive: 'sessionEnrollments',
  dismissUserBanner: 'sessionEnrollments',

  // Program waitlist — transfer record management (Wave 2)
  createTransferRecord: 'admin',
  mutateTransferRecord: 'admin',
  // Warm-on-use only (2026-09-29). '' = deployed WITHOUT a group prefix.
  addProgramWaitlistEntry: 'admin',
  exportBankTransferData: 'payment',
  listPaymentShortLinks: '',
  getTargetExistingBookings: 'admin',
  backfillTransferRecordDiscriminator: 'admin',

  // Notes
  createAttendanceNote: 'notes',
  getAttendanceNotesByClass: 'notes',
  getAttendanceNotesHistory: 'notes',
  obsoleteAttendanceNote: 'notes',
  getAttendanceNotesByGroup: 'notes',
  getAttendanceNotesByDate: 'notes',
  createProgressNote: 'notes',
  getProgressNotesByStudent: 'notes',
  getProgressNotesByClass: 'notes',
  obsoleteProgressNote: 'notes',
  getProgressNoteCounts: 'notes',
  updateTimeSlotNote: 'notes',
  getTimeSlotNote: 'notes',
  getStudentDashboardData: 'notes',
  getStudentRecordsSummary: 'notes',
  getBatchStudentRecordsSummary: 'notes',
  getTimeslotNoteHistoryByGroup: 'notes', // Forcing TS re-evaluation
  obsoleteTimeslotNoteHistoryEntry: 'notes', // Forcing TS re-evaluation
};

/**
 * `opts.timeoutMs` (W10.5 X12, 2026-10-15): the client deadline for a callable whose server
 * limit is longer than the SDK's 70 s — the same option useFirebaseFunctions().callFunction
 * takes (the SDK timer + the callDeadline backstop sized from it); omitted = the SDK default,
 * exactly as before. Nothing else changes (no transient retry is added here).
 */
export const getCallableFunction = (name: FunctionName, opts?: { timeoutMs?: number }) => {
  const group = functionGroups[name];
  if (group === undefined) {
    throw new Error(`Function '${name}' does not have a group defined.`);
  }
  // '' = deployed WITHOUT a group prefix (same convention as warmCallable) —
  // review L4: a falsy check here made a mapped ungrouped name throw at runtime.
  const fullName = group ? `${group}-${name}` : name;
  const callable =
    opts?.timeoutMs === undefined
      ? httpsCallable(functions, fullName)
      : httpsCallable(functions, fullName, { timeout: opts.timeoutMs });

  // Return a wrapped function that matches the HttpsCallable signature but adds retry logic
  return async (data?: any) => {
    // Same deadline treatment as the modern useFirebaseFunctions path: the SDK
    // arms its own timer only AFTER token acquisition, so a stalled token here
    // spins forever with no request sent. See src/utils/callDeadline.ts.
    const deadlineMs = callDeadlineFor(opts?.timeoutMs);
    try {
      await warmAuthToken(name);
      const result = await withDeadline(callable(data), deadlineMs, name);
      return result;
    } catch (error: any) {
      // Check for unauthenticated error code (usually 'functions/unauthenticated' or 401)
      if (
        error?.code === 'functions/unauthenticated' ||
        error?.message?.includes('unauthenticated')
      ) {
        console.warn(
          `[getCallableFunction] Auth error calling ${name}. Attempting token refresh...`,
        );

        try {
          // Attempt silent token refresh
          if (auth.currentUser) {
            await withDeadline(
              auth.currentUser.getIdToken(true),
              TOKEN_WARMUP_DEADLINE_MS,
              `${name} (token refresh)`,
            );
            console.log('[getCallableFunction] Token refreshed successfully. Retrying call...');

            // Retry the function call once
            const result = await withDeadline(callable(data), deadlineMs, name);
            return result;
          } else {
            console.warn('[getCallableFunction] No current user to refresh token.');
            throw new AuthError('User not logged in');
          }
        } catch (retryError) {
          console.error(`[getCallableFunction] Retry failed for ${name}:`, retryError);
          // A stall is not an expired session — surface it as a timeout, not a
          // misleading "please log in again". Mirrors useFirebaseFunctions.
          if (retryError instanceof CallTimeoutError) throw retryError;
          // Throw specific AuthError to be caught by UI
          throw new AuthError('Session expired');
        }
      }

      // Re-throw other errors
      throw error;
    }
  };
};

/** When each callable was last warm-pinged (ms of the caller's clock). */
const lastWarmPingAt = new Map<FunctionName, number>();

/**
 * Warm-on-use: fire-and-forget `{ __warmup: true }` so the NEXT call to `name`
 * finds a warm container (e.g. the class list pre-warms the student list).
 * The target returns WARMUP_RESPONSE before auth / Firestore
 * (functions utils/warmup.ts). Only during FRONTEND_API_CONFIG.WARM_ON_USE
 * hours (Taipei), at most once per MIN_INTERVAL_MS per callable, stamped when
 * sent. Never throws, never reports: a failed ping just leaves it cold.
 * `now` = debugTimeStore.getCurrentTime() at call sites (QA time travel).
 */
export const warmCallable = (name: FunctionName, now: Date): void => {
  const group = functionGroups[name];
  if (group === undefined) return;
  // '' marks a top-level export (e.g. listPaymentShortLinks) — no group prefix.
  const deployedName = group ? `${group}-${name}` : name;
  const nowMs = now.getTime();
  if (
    !shouldSendWarmPing(
      getTaipeiHour(now),
      nowMs,
      lastWarmPingAt.get(name),
      FRONTEND_API_CONFIG.WARM_ON_USE,
    )
  ) {
    return;
  }
  lastWarmPingAt.set(name, nowMs);
  try {
    void httpsCallable(functions, deployedName)({ __warmup: true }).catch(() => undefined);
  } catch {
    // A warm-up ping must never surface an error.
  }
};
